import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";

import IconButton from "features/components/IconButton";

interface SuggestionsProps {
  suggestions: string[];
  isStreaming: boolean;
  onSuggestionClick: (suggestion: string) => void;
  refreshTestId?: string;
  // props for followup mode
  onRefresh?: () => void;
  isRefreshing?: boolean;
  // props for initial mode
  maxShownLimit?: number;
}

const Suggestions = ({
  suggestions,
  isStreaming,
  onSuggestionClick,
  onRefresh,
  isRefreshing,
  maxShownLimit = 2,
  refreshTestId = "duck-refresh-suggestions-cta",
}: SuggestionsProps) => {
  const { t } = useTranslation("duck");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  if (suggestions.length === 0) {
    return null;
  }

  const displayedSuggestions = maxShownLimit
    ? (() => {
        const displayed = [];
        const limit = Math.min(maxShownLimit, suggestions.length);
        for (let i = 0; i < limit; i++) {
          const index = (currentIndex + i) % suggestions.length;
          displayed.push(suggestions[index]);
        }

        return displayed;
      })()
    : suggestions;

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else if (maxShownLimit) {
      // internal suggestion cycling (initial mode)
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setCurrentIndex((currentIndex + maxShownLimit) % suggestions.length);
      }, 500);
    }
  };

  // show refresh button when:
  // 1. external refresh is provided (followup mode - always show) OR
  // 2. limitDisplay is set and there are more suggestions than the limit
  const showRefreshButton =
    !!onRefresh || (!!maxShownLimit && suggestions.length > maxShownLimit);

  return (
    <Box className="mb-2">
      <Slide
        in={true}
        direction="up"
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 220, exit: 120 }}
      >
        <Box className="flex flex-row text-xs text-gray-700 font-sans w-full gap-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {displayedSuggestions.map((suggestion, index) => {
              const actualIndex = maxShownLimit
                ? (currentIndex + index) % suggestions.length
                : index;

              return (
                <div className="min-w-0" key={index}>
                  <button
                    type="button"
                    key={`${actualIndex}-${suggestion}`}
                    onClick={() => onSuggestionClick(suggestion)}
                    className={classNames(
                      "max-w-full truncate hover:whitespace-normal text-sm bg-purple-50 hover:bg-purple-100 rounded-sm px-3 py-1.5 text-left block"
                    )}
                    disabled={isStreaming}
                  >
                    {suggestion}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col justify-end min-w-8">
            {showRefreshButton && (
              <IconButton
                onClick={handleRefresh}
                disabled={isStreaming || isRefreshing}
                data-testid={refreshTestId}
                size="small"
                color="secondary"
                tooltip={t("refresh_suggestions")}
              >
                <AutorenewRoundedIcon
                  fontSize="medium"
                  className={classNames({
                    "animate-spin": isRefreshing || isLoading,
                  })}
                />
              </IconButton>
            )}
          </div>
        </Box>
      </Slide>
    </Box>
  );
};

export default Suggestions;
